import { logger } from "../utils/logger";
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router';
import { Mail, Phone, MapPin, Linkedin, Youtube, Star, ArrowRight, Facebook, Twitter, Instagram, Globe } from 'lucide-react';
import { Logo } from './Logo';
import { NewsletterSignup } from './NewsletterSignup';
import { API_BASE_URL, getApiHeaders } from '../utils/apiConfig';

const API_BASE = API_BASE_URL;

// ============================================
// MODULE-LEVEL CONSTANTS
// Never recreated on render.
// ============================================

const CURRENT_YEAR = new Date().getFullYear();

const SOCIAL_ICON_MAP: Record<string, React.ElementType> = {
  linkedin:  Linkedin,
  youtube:   Youtube,
  facebook:  Facebook,
  twitter:   Twitter,
  instagram: Instagram,
  globe:     Globe,
};

const SOCIAL_COLOR_MAP: Record<string, { from: string; to: string; hover: string }> = {
  linkedin:  { from: '#0A66C2', to: '#0e76d4', hover: '#0A66C2' },
  youtube:   { from: '#FF0000', to: '#cc0000', hover: '#FF0000' },
  facebook:  { from: '#1877F2', to: '#0d64d8', hover: '#1877F2' },
  twitter:   { from: '#1DA1F2', to: '#0c8ed9', hover: '#1DA1F2' },
  instagram: { from: '#E1306C', to: '#C13584', hover: '#E1306C' },
};

const DEFAULT_SOCIAL_COLOR = { from: '#888', to: '#666', hover: '#888' };

function getSocialIcon(iconName: string): React.ElementType {
  return SOCIAL_ICON_MAP[iconName.toLowerCase()] || Globe;
}

function getSocialColor(iconName: string) {
  return SOCIAL_COLOR_MAP[iconName.toLowerCase()] || DEFAULT_SOCIAL_COLOR;
}

// ============================================
// INTERFACES
// ============================================

interface FooterTopSection {
  branding: {
    companyName: string;
    tagline: string;
    description: string;
    logo: {
      lightMode: string;
      darkMode?: string;
      altText: string;
      width?: number;
      height?: number;
    };
    showLogo: boolean;
  };
  trustBadge: {
    enabled: boolean;
    rating: string;
    reviewCount: string;
    platform: string;
  };
  cta: {
    enabled: boolean;
    title: string;
    description: string;
    buttonText: string;
    buttonLink: string;
  };
}

interface FooterLink {
  id: string;
  label: string;
  href: string;
  order: number;
}

interface ContactInfo {
  email: string;
  phone: string;
  address: string;
}

interface FooterColumn {
  id: string;
  title: string;
  type: 'links' | 'contact';
  linkMode: 'manual' | 'auto-services' | 'auto-industries';
  links: FooterLink[];
  contactInfo?: ContactInfo;
  order: number;
  visible: boolean;
}

interface SocialLink {
  id: string;
  platform: string;
  url: string;
  icon: string;
  visible: boolean;
  order: number;
}

interface Newsletter {
  enabled: boolean;
  title: string;
  description: string;
  placeholder: string;
  buttonText: string;
}

interface LegalLink {
  id: string;
  label: string;
  slug: string;
  order: number;
}

interface Copyright {
  text: string;
  showCopyright: boolean;
}

interface Disclaimer {
  enabled: boolean;
  text: string;
}

// ============================================
// SKELETON
// ============================================

function FooterSkeleton() {
  return (
    <footer className="relative bg-gradient-to-br from-[#2F2E5C] via-[#3d3b6e] to-[#1a1935] text-white overflow-hidden">
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 space-y-12">
        {/* Top section skeleton */}
        <div className="grid lg:grid-cols-2 gap-8 pb-12 border-b border-white/10">
          <div className="space-y-4">
            <div className="h-10 w-40 bg-white/10 rounded-lg relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
            <div className="space-y-2">
              <div className="h-4 bg-white/10 rounded w-full relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
              <div className="h-4 bg-white/10 rounded w-5/6 relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
              <div className="h-4 bg-white/10 rounded w-4/5 relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
            </div>
            <div className="h-16 w-48 bg-white/10 rounded-2xl relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
          </div>
          <div className="h-40 bg-white/10 rounded-3xl relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        </div>
        {/* Columns skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 pb-12 border-b border-white/10">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="space-y-3">
              <div className="h-4 w-24 bg-white/10 rounded relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
              {[1, 2, 3, 4].map(j => (
                <div key={j} className="h-3 bg-white/10 rounded relative overflow-hidden" style={{ width: `${60 + j * 8}%` }}>
                  <div className="absolute inset-0 shimmer" />
                </div>
              ))}
            </div>
          ))}
        </div>
        {/* Bottom skeleton */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="h-4 w-64 bg-white/10 rounded relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
          <div className="flex gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-4 w-20 bg-white/10 rounded relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

// ============================================
// COMPONENT
// ============================================

export function Footer() {
  const [topSection, setTopSection]     = useState<FooterTopSection | null>(null);
  const [columns, setColumns]           = useState<FooterColumn[]>([]);
  const [socialLinks, setSocialLinks]   = useState<SocialLink[]>([]);
  const [newsletter, setNewsletter]     = useState<Newsletter>({ enabled: false, title: '', description: '', placeholder: '', buttonText: '' });
  const [legalLinks, setLegalLinks]     = useState<LegalLink[]>([]);
  const [copyright, setCopyright]       = useState<Copyright>({ text: '', showCopyright: false });
  const [disclaimer, setDisclaimer]     = useState<Disclaimer>({ enabled: false, text: '' });
  const [loading, setLoading]           = useState(true);

  const headers = getApiHeaders();

  // ============================================
  // FETCH HELPERS
  // ============================================

  const fetchServicesLinks = useCallback(async (): Promise<FooterLink[]> => {
    try {
      const response = await fetch(`${API_BASE}/taxonomies/services`, { headers });
      if (!response.ok) return [];
      const result = await response.json();
      if (!result.success || !result.data) return [];
      return result.data
        .filter((s: any) => s.visible !== false)
        .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
        .map((s: any) => ({ id: `auto-service-${s.id}`, label: s.title, href: s.slug, order: s.order || 0 }));
    } catch {
      return [];
    }
  }, []);

  const fetchIndustriesLinks = useCallback(async (): Promise<FooterLink[]> => {
    try {
      const response = await fetch(`${API_BASE}/industries/registry/list`, { headers });
      if (!response.ok) return [];
      const result = await response.json();
      if (!result.success || !result.data) return [];
      return result.data
        .filter((i: any) => i.visible !== false)
        .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
        .map((i: any) => {
          const slug = i.slug || '';
          const href = slug.startsWith('/industries/') ? slug : `/industries/${slug}`;
          return { id: `auto-industry-${i.id}`, label: i.title, href, order: i.order || 0 };
        });
    } catch {
      return [];
    }
  }, []);

  const resolveAutoPopulatedColumns = useCallback(async (cols: FooterColumn[]): Promise<FooterColumn[]> => {
    return Promise.all(
      cols.map(async col => {
        if (col.linkMode === 'auto-services') {
          return { ...col, links: await fetchServicesLinks() };
        }
        if (col.linkMode === 'auto-industries') {
          return { ...col, links: await fetchIndustriesLinks() };
        }
        return col;
      })
    );
  }, [fetchServicesLinks, fetchIndustriesLinks]);

  const fetchFooterData = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(`${API_BASE}/footer/published`, {
        headers,
        signal: controller.signal,
      }).finally(() => clearTimeout(timeoutId));

      if (!response.ok) {
        logger.error('[Footer] Failed to load footer:', response.status, response.statusText);
        return;
      }

      const result = await response.json();

      if (result.success && result.data?.configData) {
        const cfg = result.data.configData;

        setTopSection({
          branding:   cfg.branding,
          trustBadge: cfg.trustBadge,
          cta:        cfg.cta,
        });

        const visibleColumns = (cfg.columns || [])
          .filter((col: FooterColumn) => col.visible)
          .sort((a: FooterColumn, b: FooterColumn) => a.order - b.order);

        const resolvedColumns = await resolveAutoPopulatedColumns(visibleColumns);
        setColumns(resolvedColumns);

        const visibleSocialLinks = (cfg.socialLinks || [])
          .filter((l: SocialLink) => l.visible)
          .sort((a: SocialLink, b: SocialLink) => a.order - b.order);
        setSocialLinks(visibleSocialLinks);

        setNewsletter(cfg.newsletter);
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        logger.error('[Footer] Error loading footer:', error.message);
      }
    }
  }, [resolveAutoPopulatedColumns]);

  const fetchLegalLinks = useCallback(async () => {
    try {
      const [metadataResponse, pagesResponse] = await Promise.all([
        fetch(`${API_BASE}/legal/metadata`, { headers }),
        fetch(`${API_BASE}/legal/pages`, { headers }),
      ]);

      if (metadataResponse.ok) {
        const result = await metadataResponse.json();
        if (result.success && result.data) {
          if (result.data.copyright)  setCopyright(result.data.copyright);
          if (result.data.disclaimer) setDisclaimer(result.data.disclaimer);
        }
      }

      if (pagesResponse.ok) {
        const result = await pagesResponse.json();
        if (result.success && result.data) {
          setLegalLinks(
            result.data.map((page: any) => ({
              id:    page.id,
              label: page.title,
              slug:  page.slug,
              order: page.order,
            }))
          );
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        logger.error('[Footer] Error loading legal data:', error.message);
      }
    }
  }, []);

  // ============================================
  // SINGLE useEffect — all fetches in parallel
  // ============================================

  useEffect(() => {
    Promise.all([fetchFooterData(), fetchLegalLinks()])
      .finally(() => setLoading(false));
  }, [fetchFooterData, fetchLegalLinks]);

  // ============================================
  // FALLBACK VALUES
  // ============================================

  const branding = topSection?.branding || {
    description: 'Transforming complex ideas into compelling visual stories. Trusted by 280+ global businesses across 18+ industries.',
    showLogo: true,
  };

  const trustBadge = topSection?.trustBadge || {
    enabled: true,
    rating: '5.0',
    reviewCount: '280+ reviews',
    platform: 'Google Reviews',
  };

  const cta = topSection?.cta || {
    enabled: true,
    title: 'Ready to Get Started?',
    description: "Let's create impactful animations that drive real business results.",
    buttonText: 'Get a Free Quote',
    buttonLink: '/contact',
  };

  // ============================================
  // RENDER
  // ============================================

  if (loading) return <FooterSkeleton />;

  return (
    <footer className="relative bg-gradient-to-br from-[#2F2E5C] via-[#3d3b6e] to-[#1a1935] text-white overflow-hidden">
      {/* Animated gradient orbs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-[#E8B84D]/20 via-[#E87D3E]/15 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-[#D74444]/15 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDuration: '5s' }} />
      <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-gradient-to-br from-[#2F2E5C]/30 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s' }} />
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' /%3E%3C/svg%3E")'}} />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">

        {/* ── Top Section ── */}
        <div className="mb-12 pb-12 border-b border-white/10">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Logo & Rating */}
            <div>
              {branding.showLogo && (
                <div className="mb-4">
                  {topSection?.branding?.logo?.darkMode || topSection?.branding?.logo?.lightMode ? (
                    <img
                      src={topSection.branding.logo.darkMode || topSection.branding.logo.lightMode}
                      alt={topSection.branding.logo.altText || 'Company Logo'}
                      style={{
                        width:  topSection.branding.logo.width  ? `${topSection.branding.logo.width}px`  : 'auto',
                        height: topSection.branding.logo.height ? `${topSection.branding.logo.height}px` : '40px',
                      }}
                    />
                  ) : (
                    <Logo variant="white" className="h-10 w-auto" />
                  )}
                </div>
              )}

              <p className="text-white/80 mb-6 max-w-md leading-relaxed">{branding.description}</p>

              {trustBadge.enabled && (
                <div className="relative inline-block">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#E8B84D]/20 to-[#E87D3E]/20 rounded-2xl blur-xl" />
                  <div className="relative bg-white/5 backdrop-blur-xl rounded-2xl px-5 py-4 border border-white/10 shadow-xl">
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map(star => (
                          <Star key={star} className="w-5 h-5 fill-[#E8B84D] text-[#E8B84D] drop-shadow-lg" />
                        ))}
                      </div>
                      <div className="h-6 w-px bg-white/20" />
                      <div>
                        <div className="text-xl font-bold text-white">{trustBadge.rating}</div>
                        <div className="text-xs text-white/70">{trustBadge.reviewCount}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* CTA Card */}
            {cta.enabled && (
              <div className="relative group/cta">
                <div className="absolute inset-0 bg-gradient-to-br from-[#D74444]/30 to-[#E87D3E]/30 rounded-3xl blur-2xl opacity-60 group-hover/cta:opacity-80 transition-opacity" />
                <div className="relative bg-white/10 backdrop-blur-2xl rounded-3xl p-6 sm:p-8 border-2 border-white/20 shadow-2xl">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#E8B84D]/20 to-transparent rounded-full blur-2xl" />
                  <div className="relative">
                    <h3 className="text-2xl font-bold text-white mb-2">{cta.title}</h3>
                    <p className="text-white/80 mb-6 text-sm leading-relaxed">{cta.description}</p>
                    <Link to={cta.buttonLink} className="group/btn relative inline-flex items-center gap-2 overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] rounded-full blur-lg opacity-50 group-hover/btn:opacity-75 transition-opacity" />
                      <div className="relative bg-gradient-to-r from-[#D74444] to-[#E87D3E] hover:from-[#E87D3E] hover:to-[#E8B84D] text-white px-6 py-3.5 rounded-full transition-all shadow-xl flex items-center gap-2">
                        <span className="font-bold">{cta.buttonText}</span>
                        <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Main Link Columns ── */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 mb-12">
          {columns.map(column => (
            <div key={column.id} className="group/column">
              <h3 className="text-white font-bold mb-4 text-sm flex items-center gap-2">
                {column.title}
                <div className="h-px flex-1 bg-gradient-to-r from-white/20 to-transparent" />
              </h3>

              {column.type === 'links' && (
                <ul className="space-y-2.5">
                  {column.links.map(link => (
                    <li key={link.id}>
                      <Link to={link.href} className="group/link relative inline-flex items-center gap-2 text-white/70 hover:text-white transition-all text-sm">
                        <span className="absolute -left-2 w-1 h-1 bg-[#E87D3E] rounded-full opacity-0 group-hover/link:opacity-100 transition-opacity" />
                        <span className="group-hover/link:translate-x-2 transition-transform">{link.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}

              {column.type === 'contact' && column.contactInfo && (
                <ul className="space-y-3">
                  {column.contactInfo.email && (
                    <li className="group/contact flex items-start gap-2">
                      <div className="relative mt-0.5">
                        <div className="absolute inset-0 bg-[#E87D3E] rounded blur-md opacity-0 group-hover/contact:opacity-50 transition-opacity" />
                        <Mail className="relative w-4 h-4 text-[#E87D3E] flex-shrink-0" />
                      </div>
                      <a href={`mailto:${column.contactInfo.email}`} className="text-white/70 hover:text-white transition-colors text-sm">
                        {column.contactInfo.email}
                      </a>
                    </li>
                  )}
                  {column.contactInfo.phone && (
                    <li className="group/contact flex items-start gap-2">
                      <div className="relative mt-0.5">
                        <div className="absolute inset-0 bg-[#E87D3E] rounded blur-md opacity-0 group-hover/contact:opacity-50 transition-opacity" />
                        <Phone className="relative w-4 h-4 text-[#E87D3E] flex-shrink-0" />
                      </div>
                      <a href={`tel:${column.contactInfo.phone.replace(/[^0-9+]/g, '')}`} className="text-white/70 hover:text-white transition-colors text-sm">
                        {column.contactInfo.phone}
                      </a>
                    </li>
                  )}
                  {column.contactInfo.address && (
                    <li className="group/contact flex items-start gap-2">
                      <div className="relative mt-0.5">
                        <div className="absolute inset-0 bg-[#E87D3E] rounded blur-md opacity-0 group-hover/contact:opacity-50 transition-opacity" />
                        <MapPin className="relative w-4 h-4 text-[#E87D3E] flex-shrink-0" />
                      </div>
                      <span
                        className="text-white/70 text-sm"
                        dangerouslySetInnerHTML={{ __html: column.contactInfo.address.replace(/\n/g, '<br />') }}
                      />
                    </li>
                  )}
                </ul>
              )}
            </div>
          ))}
        </div>

        {/* ── Social & Newsletter ── */}
        <div className="mb-12 pb-12 border-b border-white/10">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-white font-bold mb-3 text-sm">Follow Us</h3>
              <div className="flex items-center gap-3">
                {socialLinks.map(link => {
                  const IconComponent = getSocialIcon(link.icon);
                  const colors = getSocialColor(link.icon);
                  return (
                    <a
                      key={link.id}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group/social relative w-12 h-12 flex items-center justify-center transition-all"
                      aria-label={link.platform}
                    >
                      <div
                        className="absolute inset-0 rounded-xl blur-lg opacity-0 group-hover/social:opacity-60 transition-opacity"
                        style={{ background: `linear-gradient(to bottom right, ${colors.from}, ${colors.to})` }}
                      />
                      {/* Use CSS class hover instead of inline onMouseEnter/Leave */}
                      <div
                        className="social-icon-btn relative w-full h-full bg-white/10 backdrop-blur-xl rounded-xl flex items-center justify-center transition-all group-hover/social:scale-110 border border-white/20"
                        style={{ '--hover-color': colors.hover } as React.CSSProperties}
                      >
                        <IconComponent className="w-5 h-5 text-white" />
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>

            {newsletter.enabled && (
              <div>
                <h3 className="text-white font-bold mb-3 text-sm">{newsletter.title || 'Stay Updated'}</h3>
                <p className="text-white/70 text-sm mb-4">{newsletter.description || 'Get exclusive insights and animation tips delivered monthly.'}</p>
                <NewsletterSignup variant="footer" showTitle={false} />
              </div>
            )}
          </div>
        </div>

        {/* ── Bottom Bar ── */}
        <div className="space-y-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left">
            {copyright.showCopyright && (
              <div className="text-white/60 text-xs sm:text-sm">
                {copyright.text.replace('{year}', CURRENT_YEAR.toString())}
              </div>
            )}
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-xs sm:text-sm">
              {legalLinks.map((link, index) => (
                <React.Fragment key={link.id}>
                  {index > 0 && <span className="text-white/30">|</span>}
                  <Link to={`/${link.slug}`} className="text-white/60 hover:text-white transition-colors">
                    {link.label}
                  </Link>
                </React.Fragment>
              ))}
            </div>
          </div>

          {disclaimer.enabled && disclaimer.text && (
            <div className="text-white/40 text-xs leading-relaxed text-center md:text-left pt-4 border-t border-white/5">
              {disclaimer.text}
            </div>
          )}
        </div>
      </div>
    </footer>
  );
}